import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Chip,
  LinearProgress,
  Alert
} from '@mui/material';
import {
  TrendingUp,
  LocalOffer,
  ShoppingCart,
  Timer,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import OfferService from '../../services/offers';
import { ccyFormat } from '../../helpers/helper';

const OfferStatsModal = ({ open, onClose, offerId, offerName }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await OfferService.getStats(offerId);
      setStats(data);
    } catch (err) {
      setError('Error al cargar las estadísticas de la oferta');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && offerId) {
      loadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, offerId]);

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary', gradient }) => (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        height: '100%',
        background: gradient || `linear-gradient(135deg, ${color === 'primary' ? '#667eea 0%, #764ba2 100%' : color === 'success' ? '#56ab2f 0%, #a8e063 100%' : color === 'error' ? '#eb3349 0%, #f45c43 100%' : '#f093fb 0%, #f5576c 100%'})`,
        color: 'white',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500 }}>
            {title}
          </Typography>
          {Icon && <Icon sx={{ opacity: 0.7, fontSize: 28 }} />}
        </Box>
        <Typography variant="h4" fontWeight="bold" mb={0.5}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: -20,
          right: -20,
          opacity: 0.1,
          fontSize: 120
        }}
      >
        {Icon && <Icon sx={{ fontSize: 'inherit' }} />}
      </Box>
    </Paper>
  );

  const InfoRow = ({ label, value, chip = false, chipColor = 'default' }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" py={1}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {chip ? (
        <Chip label={value} color={chipColor} size="small" />
      ) : (
        <Typography variant="body2" fontWeight="600">
          {value}
        </Typography>
      )}
    </Box>
  );

  if (!stats && !loading && !error) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LocalOffer color="primary" />
          <Box>
            <Typography variant="h6">Estadísticas de Oferta</Typography>
            <Typography variant="caption" color="text.secondary">
              {offerName || stats?.offer_name}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress size={60} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {stats && !loading && (
          <Box>
            {/* Estado de la oferta */}
            <Box mb={3}>
              <Box display="flex" gap={1} mb={2}>
                <Chip
                  icon={stats.active ? <CheckCircle /> : <Cancel />}
                  label={stats.active ? 'Activa' : 'Inactiva'}
                  color={stats.active ? 'success' : 'default'}
                  size="small"
                />
                <Chip
                  label={stats.is_expired ? 'Expirada' : 'Vigente'}
                  color={stats.is_expired ? 'error' : 'success'}
                  size="small"
                />
                {stats.days_remaining !== undefined && stats.days_remaining >= 0 && !stats.is_expired && (
                  <Chip
                    icon={<Timer />}
                    label={`${stats.days_remaining} días restantes`}
                    color="info"
                    size="small"
                  />
                )}
                <Chip
                  label={`${stats.days_active} días activa`}
                  color="default"
                  size="small"
                />
              </Box>
            </Box>

            {/* Tarjetas principales de estadísticas */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total de Usos"
                  value={stats.total_uses}
                  subtitle={`${stats.current_uses} veces aplicada`}
                  icon={ShoppingCart}
                  color="primary"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Ingresos Generados"
                  value={`$${ccyFormat(stats.total_revenue || 0)}`}
                  subtitle="Ventas con esta oferta"
                  icon={TrendingUp}
                  color="success"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Descuento Otorgado"
                  value={`$${ccyFormat(stats.total_discount || 0)}`}
                  subtitle={`Promedio: $${ccyFormat(stats.avg_discount_per_use || 0)}`}
                  icon={LocalOffer}
                  gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Items Vendidos"
                  value={stats.total_items_sold}
                  subtitle="Productos con descuento"
                  icon={ShoppingCart}
                  color="error"
                />
              </Grid>
            </Grid>

            {/* Progreso de uso */}
            {stats.max_uses_total > 0 && (
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight="600">
                    Progreso de Uso
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.current_uses} / {stats.max_uses_total} usos
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.usage_percentage || 0}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: 5
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" mt={0.5}>
                  {stats.usage_percentage?.toFixed(1)}% utilizado
                </Typography>
              </Paper>
            )}

            {/* Comparación de montos */}
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight="600" mb={2}>
                Análisis Financiero
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Monto Original
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      ${ccyFormat(stats.total_original_amount || 0)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: '#fff3e0', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Descuento Aplicado
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="error.main">
                      -${ccyFormat(stats.total_discount || 0)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: '#e8f5e9', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Ingresos Finales
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="success.main">
                      ${ccyFormat(stats.total_revenue || 0)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              {stats.total_original_amount > 0 && (
                <Box mt={2} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Tasa de descuento efectiva: {' '}
                    <Typography component="span" fontWeight="bold" color="primary">
                      {((stats.total_discount / stats.total_original_amount) * 100).toFixed(2)}%
                    </Typography>
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Detalles de la oferta */}
            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight="600" mb={2}>
                Detalles de la Oferta
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="Tipo de Oferta" value={stats.offer_type} chip chipColor="primary" />
              <Divider />
              <InfoRow
                label="Fecha de Inicio"
                value={stats.start_date ? format(new Date(stats.start_date), 'dd/MM/yyyy HH:mm', { locale: es }) : 'N/A'}
              />
              <Divider />
              <InfoRow
                label="Fecha de Fin"
                value={stats.end_date ? format(new Date(stats.end_date), 'dd/MM/yyyy HH:mm', { locale: es }) : 'N/A'}
              />
              <Divider />
              <InfoRow
                label="Promedio por Uso"
                value={`$${ccyFormat(stats.avg_discount_per_use || 0)}`}
              />
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfferStatsModal;
